import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Preloader from "../helper/Preloader";
import ColorInit from "../helper/ColorInit";
import HeaderOne from "../components/HeaderOne";
import Breadcrumb from "../components/Breadcrumb";
import FooterOne from "../components/FooterOne";
import BottomFooter from "../components/BottomFooter";
import ShippingOne from "../components/ShippingOne";
import ScrollToTop from "react-scroll-to-top";
import { toast } from "react-toastify";
import orderService from "../services/orderService";
import { PaymentMethod, OrderStatus, OrderStatusLabels } from "../constants/enums";
import { useCart } from "../contexts/CartContext";
import { formatQuantityWithUnit } from "../utils/unitHelpers";

// OrderStatus enum değerleri (Domain/Entities/Order/OrderStatus.cs dosyasındaki değerlerle eşleşiyor)
const OrderSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const { clearCart } = useCart();
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    // URL'den order ID ve status parametrelerini alıp state'e kaydediyoruz
    const params = new URLSearchParams(location.search);
    const orderIdFromUrl = params.get("orderId");
    const statusFromUrl = params.get("status");
    setOrderId(orderIdFromUrl);
    
    // Eğer status=success varsa, ödeme başarılı demektir - sepeti hemen temizle (hem giriş yapmış hem yapmamış kullanıcılar için)
    if (statusFromUrl === "success" && orderIdFromUrl) {
      clearCart().catch((error) => {
        console.error("Sepet temizlenirken hata oluştu:", error);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        if (!orderId) {
          setLoading(false);
          return;
        }

        setLoading(true);

        const orderResult = await orderService.getOrderDetails(orderId);

        if (!orderResult.succeeded || !orderResult.order) {
          throw new Error(orderResult.message || "Sipariş detayları alınamadı");
        }

        setOrderDetails(orderResult.order);
        
        // Sipariş durumunu kontrol et - başarılı ödeme durumları için sepeti temizle
        const orderStatus = orderResult.order.status;
        const successfulPaymentStatuses = [
          OrderStatus.PaymentCompleted,
          OrderStatus.Processing,
          OrderStatus.Shipped,
          OrderStatus.Delivered
        ];
        
        // Eğer sipariş başarılı bir ödeme durumundaysa sepeti temizle
        // (Bu, önceki clearCart çağrısının tekrar çalışmasını önlemek için kontrol edilir)
        if (successfulPaymentStatuses.includes(orderStatus)) {
          await clearCart();
        }
      } catch (error) {
        console.error("Sipariş detayları alınırken hata:", error);
        toast.error(
          error.message || "Sipariş detayları yüklenirken bir hata oluştu"
        );
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, navigate]);

  // Ödeme yöntemine göre bilgi mesajı
  const getPaymentMethodMessage = () => {
    // State'ten gelen özel mesaj varsa onu kullan
    if (location.state?.message) {
      return location.state.message;
    }

    // Yoksa ödeme yöntemine göre varsayılan mesaj göster
    const paymentMethod =
      location.state?.paymentMethod || orderDetails?.paymentMethod;

    switch (paymentMethod) {
      case PaymentMethod.BANK_TRANSFER:
        return "Banka havalesi/EFT ile ödemenizi gerçekleştirdikten sonra siparişiniz onaylanacaktır.";
      case PaymentMethod.CREDIT_CARD:
        return "Kredi kartı ile ödemeniz başarıyla gerçekleştirildi. Siparişiniz en kısa sürede hazırlanacaktır.";
      default:
        return "Siparişiniz başarıyla oluşturuldu. Siparişiniz en kısa sürede hazırlanacaktır.";
    }
  };

  // Ödeme yöntemini string olarak döndür
  const getPaymentMethodText = () => {
    const paymentMethod =
      location.state?.paymentMethod || orderDetails?.paymentMethod;

    switch (paymentMethod) {
      case PaymentMethod.BANK_TRANSFER:
        return "Banka Havalesi/EFT";
      case PaymentMethod.CREDIT_CARD:
        return "Kredi Kartı";
      case PaymentMethod.CASH_ON_DELIVERY:
        return "Kapıda Ödeme";
      default:
        return "Online Ödeme";
    }
  };

  // Sipariş durumunu metin olarak döndür
  const getOrderStatusText = () => {
    const status = orderDetails?.status;
    
    if (status === null || status === undefined) {
      return "Onay Bekliyor";
    }
    
    return OrderStatusLabels[status] || "Onay Bekliyor";
  };

  // Sipariş durum rengini belirle
  const getOrderStatusColor = () => {
    const status = orderDetails?.status;

    switch (status) {
      case OrderStatus.Pending:
      case OrderStatus.BankTransferPending:
      case OrderStatus.PaymentWaiting3DSecure:
        return "text-warning";
      case OrderStatus.Processing:
      case OrderStatus.PaymentCompleted:
        return "text-info";
      case OrderStatus.Shipped:
        return "text-primary";
      case OrderStatus.Delivered:
        return "text-success";
      case OrderStatus.Canceled:
      case OrderStatus.PaymentFailed:
        return "text-danger";
      case OrderStatus.Returned:
      case OrderStatus.Refunded:
        return "text-secondary";
      default:
        return "text-warning";
    }
  };

  // Fiyatı biçimlendir
  const formatPrice = (price) => {
    if (!price && price !== 0) return "-";
    return `${price.toFixed(2)} ₺`;
  };

  // Adres bilgisini düzenli göster
  const formatAddress = (addressData) => {
    if (!addressData) return '';
    
    // Temel adres bilgileri
    let addressParts = [];
    
    // Ana adres satırı
    if (addressData.address) {
      addressParts.push(addressData.address);
    }
    
    // Apartment/Daire bilgisi varsa ekle
    if (addressData.apartment && addressData.apartment.trim() !== '') {
      addressParts.push(addressData.apartment);
    }
    
    // İlçe ve İl bilgisi (arada "/" olmadan)
    let locationParts = [];
    if (addressData.district && addressData.district.trim() !== '') {
      locationParts.push(addressData.district);
    }
    
    if (addressData.city && addressData.city.trim() !== '') {
      locationParts.push(addressData.city);
    }
    
    // Posta kodu varsa ekle
    if (addressData.zipCode || addressData.postCode || addressData.postalCode) {
      const postCode = addressData.zipCode || addressData.postCode || addressData.postalCode;
      if (postCode && postCode.trim() !== '') {
        locationParts.push(postCode);
      }
    }
    
    return {
      mainAddress: addressParts.join(', '),
      location: locationParts.join(' ')
    };
  };

  // Telefon numarasını formatlama
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    
    // Temizleme (sadece rakamlar kalsın)
    const cleanedNumber = phone.replace(/\D/g, '');
    
    // Başta 90 veya +90 varsa kaldır ve 0 ekle
    let formattedNumber = cleanedNumber;
    if (cleanedNumber.startsWith('90') && cleanedNumber.length >= 12) {
      formattedNumber = '0' + cleanedNumber.substring(2);
    } else if (!cleanedNumber.startsWith('0') && cleanedNumber.length === 10) {
      formattedNumber = '0' + cleanedNumber;
    }
    
    // 0 5XX XXX XX XX formatına dönüştür
    if (formattedNumber.length === 11 && formattedNumber.startsWith('0')) {
      return `${formattedNumber.substring(0, 1)} ${formattedNumber.substring(1, 4)} ${formattedNumber.substring(4, 7)} ${formattedNumber.substring(7, 9)} ${formattedNumber.substring(9, 11)}`;
    }
    
    // Eğer format uygun değilse, orijinal telefon numarasını döndür
    return phone;
  };

  // Sipariş durum ilerleme çubuğu
  const renderOrderStatusProgress = () => {
    const status = orderDetails?.status || "Pending";
    const statusIndex = getStatusIndex(status);

    const statusSteps = [
      { key: "Pending", label: "Onay Bekliyor", icon: "ph-clock" },
      { key: "Processing", label: "Hazırlanıyor", icon: "ph-package" },
      { key: "Shipped", label: "Kargoya Verildi", icon: "ph-truck" },
      { key: "Delivered", label: "Teslim Edildi", icon: "ph-check-circle" },
    ];

    return (
      <div className="order-tracking-progress mb-24">
        <div className="bg-light p-24 rounded-16">
          <h5 className="fw-semibold mb-16 text-heading d-flex align-items-center">
            <i className="ph ph-truck-delivery me-8 text-main-600"></i>
            Sipariş Takibi
          </h5>
          
          <div className="position-relative" style={{ padding: "20px 10px" }}>
            {/* İlerleme çizgisi */}
            <div 
              className="progress position-absolute" 
              style={{ 
                height: "3px", 
                top: "40px", // İkonların ortasına hizalanacak şekilde
                left: "40px", // İlk ikondan başlaması için
                right: "40px", // Son ikonda bitmesi için
                zIndex: "1" 
              }}
            >
              <div 
                className="progress-bar bg-main-600" 
                role="progressbar" 
                style={{ 
                  width: statusIndex === 0 ? "0%" : `${(statusIndex / (statusSteps.length - 1)) * 100}%`,
                  transition: "width 1s ease-in-out",
                  zIndex: "999"
                }} 
                aria-valuenow={statusIndex === 0 ? 0 : (statusIndex / (statusSteps.length - 1)) * 100} 
                aria-valuemin="0" 
                aria-valuemax="100"
              ></div>
            </div>
            
            {/* Adım noktaları */}
            <div className="d-flex justify-content-between position-relative" style={{ zIndex: "2" }}>
              {statusSteps.map((step, index) => {
                // Adımın durumu (geçmiş, aktif, gelecek)
                const isActive = index <= statusIndex;
                const isCurrent = index === statusIndex;
                
                return (
                  <div key={step.key} className="text-center position-relative" style={{ width: "80px" }}>
                    <div 
                      className={`rounded-circle mx-auto d-flex align-items-center justify-content-center ${
                        isActive 
                          ? isCurrent 
                            ? "bg-main-600 pulse-animation" 
                            : "bg-main-600" 
                          : "bg-gray-200"
                      }`} 
                      style={{ 
                        width: "40px", 
                        height: "40px", 
                        color: isActive ? "white" : "#adb5bd",
                        transition: "all 0.3s ease",
                        zIndex: "3"
                      }}
                    >
                      <i className={`ph ${step.icon}`}></i>
                    </div>
                    <p className={`mt-8 mb-0 small ${isActive ? isCurrent ? "fw-bold text-main-600" : "text-heading" : "text-muted"}`} style={{ fontSize: "0.75rem" }}>
                      {step.label}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="text-center mt-16">
            <p className="text-body-secondary mb-0">
              <small>
                <i className="ph ph-info me-4"></i>
                Siparişiniz {getOrderStatusText().toLowerCase()} durumunda. Güncelleme olduğunda e-posta ile bilgilendirileceksiniz.
              </small>
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Sipariş durumunun indeksini döndür
  const getStatusIndex = (status) => {
    switch (status) {
      case OrderStatus.Pending: 
      case OrderStatus.BankTransferPending: 
      case OrderStatus.PaymentWaiting3DSecure:
      case OrderStatus.PaymentFailed:
        return 0;
      case OrderStatus.Processing:
      case OrderStatus.PaymentCompleted: // PaymentCompleted'ı "Hazırlanıyor" adımına taşıyoruz
        return 1;
      case OrderStatus.Shipped:
        return 2;
      case OrderStatus.Delivered:
        return 3;
      case OrderStatus.Canceled:
      case OrderStatus.Returned:
      case OrderStatus.Refunded:
        return 0;
      default:
        return 0;
    }
  };

  // Animasyonlu onay işareti bileşeni
  const renderSuccessIcon = () => {
    return (
      <div className="success-checkmark mb-16">
        <div className="check-icon">
          <span className="icon-line line-tip"></span>
          <span className="icon-line line-long"></span>
          <div className="icon-circle"></div>
          <div className="icon-fix"></div>
        </div>
      </div>
    );
  };

  // Ödeme yöntemi ikon bileşeni
  const renderPaymentMethodIcon = () => {
    const paymentMethod = 
      location.state?.paymentMethod || orderDetails?.paymentMethod;
    
    let iconClass = "ph ph-check-circle";
    let bgClass = "bg-success bg-opacity-10";
    let textClass = "text-success";
    
    switch (paymentMethod) {
      case PaymentMethod.BANK_TRANSFER:
        iconClass = "ph ph-bank";
        bgClass = "bg-primary bg-opacity-10";
        textClass = "text-primary";
        break;
      case PaymentMethod.CREDIT_CARD:
        iconClass = "ph ph-credit-card";
        break;
      case PaymentMethod.CASH_ON_DELIVERY:
        iconClass = "ph ph-money";
        bgClass = "bg-warning bg-opacity-10";
        textClass = "text-warning";
        break;
      default:
        break;
    }

    return (
      <div className="icon-container mb-16">
        <div
          className={`${bgClass} rounded-circle d-inline-flex align-items-center justify-content-center`}
          style={{ height: "120px", width: "120px" }}
        >
          <i
            className={`${iconClass} ${textClass}`}
            style={{ fontSize: "4rem" }}
          ></i>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* ColorInit */}
      <ColorInit color={false} />

      {/* ScrollToTop */}
      <ScrollToTop smooth color="#299E60" />

      {/* Preloader */}
      <Preloader />

      {/* HeaderOne */}
      <HeaderOne />

      {/* Breadcrumb */}
      <Breadcrumb title={"Sipariş Tamamlandı"} />

      {/* Order Success Content */}
      <section className="order-success py-60">
        <div className="container container-lg">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="payment-page-card">
                <div className="card-body p-24">
                  {loading ? (
                    <div className="text-center py-32">
                      <div className="spinner-border text-main-600" role="status">
                        <span className="visually-hidden">Yükleniyor...</span>
                      </div>
                      <p className="mt-16 text-heading">Sipariş bilgileriniz yükleniyor...</p>
                    </div>
                  ) : !orderId ? (
                    <div className="text-center">
                      <div className="alert alert-danger rounded-16">
                        <div className="d-flex justify-content-center mb-12">
                          <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ height: "80px", width: "80px" }}>
                            <i className="ph ph-x text-danger" style={{ fontSize: "3rem" }}></i>
                          </div>
                        </div>
                        <h4 className="fw-semibold mb-8">Sipariş bilgisi bulunamadı</h4>
                        <p className="mb-12">URL parametresi eksik veya geçersiz.</p>
                        <Link to="/" className="btn btn-main-600 hover-main-700 px-16 py-8 rounded-8">
                          <i className="ph ph-house-line me-8"></i>
                          Ana Sayfaya Dön
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      {renderPaymentMethodIcon()}

                      <h3 className="fw-bold mb-16 text-heading">
                        Siparişiniz Alınmıştır!
                      </h3>

                      <div className="order-info mb-24">
                        <div className="order-summary-card p-24 mb-0 rounded-16">
                          <div className="row gy-12">
                            <div className="col-md-6">
                              <p className="fw-semibold mb-4 text-heading">
                            Sipariş Numarası:{" "}
                                <span className="text-main-600">
                              {orderDetails?.orderNumber}
                            </span>
                          </p>
                              <p className="mb-0 text-body-secondary">
                                Tarih: {new Date(orderDetails?.createdAt || Date.now()).toLocaleDateString('tr-TR')}
                              </p>
                            </div>
                            <div className="col-md-6">
                              <p className="fw-semibold mb-4 text-heading">
                                E-posta Adresi
                              </p>
                              <p className="mb-0 text-body-secondary">
                              {orderDetails?.shippingAddress?.email}
                          </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Sipariş Takip İlerleme Çubuğu */}
                      {renderOrderStatusProgress()}

                      <div className="order-details mb-24">
                        <div className="row g-24">
                          <div className="col-md-6">
                            <div className="bg-light p-24 rounded-16 h-100">
                              <h5 className="fw-semibold mb-12 text-heading d-flex align-items-center">
                                <i className="ph ph-map-pin me-8 text-main-600"></i>
                                Teslimat Adresi
                              </h5>
                              <div className="text-start">
                                <p className="mb-4 fw-medium text-heading">
                                  {orderDetails?.shippingAddress?.firstName} {orderDetails?.shippingAddress?.lastName}
                                </p>
                                {orderDetails?.shippingAddress && (
                                  <>
                                    <p className="mb-4 text-body-secondary">
                                      {formatAddress(orderDetails.shippingAddress).mainAddress}
                                    </p>
                                    <p className="mb-4 text-body-secondary">
                                      {formatAddress(orderDetails.shippingAddress).location}
                                    </p>
                                  </>
                                )}
                                {orderDetails?.shippingAddress?.phoneNumber || orderDetails?.shippingAddress?.phone ? (
                                  <p className="mb-0 text-body-secondary">
                                    <i className="ph ph-phone me-4"></i> {formatPhoneNumber(orderDetails.shippingAddress.phoneNumber || orderDetails.shippingAddress.phone)}
                                  </p>
                                ) : null}
                                
                                {/* Fatura Adresi Bilgisi */}
                                {orderDetails?.useSameAddressForBilling ? (
                                  <div className="mt-16 pt-16 border-top">
                                    <div className="d-flex align-items-center mb-8">
                                      <i className="ph ph-receipt me-8 text-main-600"></i>
                                      <span className="fw-medium text-heading">Fatura Adresi</span>
                                    </div>
                                    <p className="text-body-secondary mb-0">
                                      <i className="ph ph-info-circle me-4 text-main-600"></i>
                                      Fatura ve teslimat adresleriniz aynıdır.
                                    </p>
                                  </div>
                                ) : orderDetails?.billingAddress ? (
                                  <div className="mt-16 pt-16 border-top">
                                    <div className="d-flex align-items-center mb-8">
                                      <i className="ph ph-receipt me-8 text-main-600"></i>
                                      <span className="fw-medium text-heading">Fatura Adresi</span>
                                    </div>
                                    <p className="mb-4 fw-medium text-heading">
                                      {orderDetails.billingAddress.firstName} {orderDetails.billingAddress.lastName}
                                    </p>
                                    <p className="mb-4 text-body-secondary">
                                      {formatAddress(orderDetails.billingAddress).mainAddress}
                                    </p>
                                    <p className="mb-4 text-body-secondary">
                                      {formatAddress(orderDetails.billingAddress).location}
                                    </p>
                                    {orderDetails.billingAddress.phoneNumber || orderDetails.billingAddress.phone ? (
                                      <p className="mb-0 text-body-secondary">
                                        <i className="ph ph-phone me-4"></i> {formatPhoneNumber(orderDetails.billingAddress.phoneNumber || orderDetails.billingAddress.phone)}
                                      </p>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="bg-light p-24 rounded-16 h-100">
                              <h5 className="fw-semibold mb-12 text-heading d-flex align-items-center">
                                <i className="ph ph-info me-8 text-main-600"></i>
                                Sipariş Durumu
                              </h5>
                              <div className="text-start">
                                <div className="d-flex align-items-center mb-8">
                                  <span className="fw-medium text-heading me-8">Durum:</span>
                                  <span className={`badge ${getOrderStatusColor()} bg-opacity-10 px-12 py-8 rounded-pill`}>
                                    {getOrderStatusText()}
                                  </span>
                                </div>
                                <p className="mb-4 text-body-secondary">
                                  <span className="fw-medium text-heading me-4">Ödeme Yöntemi:</span> 
                                  {getPaymentMethodText()}
                                </p>
                                {orderDetails?.notes && (
                                  <p className="mb-0 text-body-secondary">
                                    <span className="fw-medium text-heading me-4">Sipariş Notu:</span> 
                                    {orderDetails.notes}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {orderDetails?.orderItems && orderDetails.orderItems.length > 0 && (
                        <div className="order-items-details mb-24">
                          <div className="bg-light p-24 rounded-16">
                            <h5 className="fw-semibold mb-16 text-heading d-flex align-items-center">
                              <i className="ph ph-shopping-bag me-8 text-main-600"></i>
                              Sipariş Özeti
                            </h5>
                            <div className="table-responsive">
                              <table className="table table-borderless mb-0">
                                <thead className="text-heading">
                                  <tr>
                                    <th className="text-start">Ürün</th>
                                    <th className="text-center">Miktar</th>
                                    <th className="text-center">Birim Fiyat</th>
                                    <th className="text-end">Toplam</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {orderDetails.orderItems.map((item, index) => (
                                    <tr key={index}>
                                      <td className="text-start">
                                        <div className="d-flex align-items-center">
                                          {item.imageUrl && (
                                            <img 
                                              src={item.imageUrl} 
                                              alt={item.productName} 
                                              className="rounded me-8" 
                                              style={{ width: "50px", height: "50px", objectFit: "cover" }} 
                                            />
                                          )}
                                          <div>
                                            <p className="mb-0 fw-medium text-heading">{item.productName}</p>
                                            {item.optionText && <small className="text-muted">{item.optionText}</small>}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="text-center">
                                        {item.weightInGrams 
                                          ? formatQuantityWithUnit(item.weightInGrams)
                                          : `${item.quantity} Adet`}
                                      </td>
                                      <td className="text-center">{formatPrice(item.unitPrice)}</td>
                                      <td className="text-end fw-semibold text-heading">{formatPrice(item.totalPrice)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot className="border-top">
                                  <tr>
                                    <td colSpan="3" className="text-end fw-medium text-heading">Ara Toplam:</td>
                                    <td className="text-end fw-semibold text-heading">{formatPrice(orderDetails.totalAmount)}</td>
                                  </tr>
                                  {orderDetails.discountAmount > 0 && (
                                    <tr>
                                      <td colSpan="3" className="text-end fw-medium text-heading">İndirim:</td>
                                      <td className="text-end fw-semibold text-success">-{formatPrice(orderDetails.discountAmount)}</td>
                                    </tr>
                                  )}
                                  <tr>
                                    <td colSpan="3" className="text-end fw-medium text-heading">Kargo Ücreti:</td>
                                    <td className="text-end fw-semibold text-heading">{formatPrice(orderDetails.shippingCost)}</td>
                                  </tr>
                                  <tr>
                                    <td colSpan="3" className="text-end fw-bold text-heading fs-5">Genel Toplam:</td>
                                    <td className="text-end fw-bold text-main-600 fs-5">{formatPrice(orderDetails.totalAmount + orderDetails.shippingCost)}</td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="payment-info mb-24">
                        <div className="alert bg-main-50 p-24 rounded-16 mb-0 border-0">
                          <div className="d-flex align-items-center">
                            <i className="ph ph-info me-12 text-main-600 fs-4"></i>
                            <p className="mb-0 text-heading">{getPaymentMethodMessage()}</p>
                          </div>
                        </div>
                      </div>

                      <div className="next-steps mt-32">
                        <p className="mb-16 text-body-secondary">
                          Siparişinizin durumunu "Hesabım" sayfasından takip
                          edebilirsiniz.
                        </p>

                        <div className="d-grid gap-16 d-md-flex justify-content-md-center">
                          <Link
                            to="/"
                            className="btn btn-lg payment-submit-btn px-24 py-12 rounded-8 d-flex justift-content-center align-items-center"
                          >
                            <i className="ph ph-house-line me-8"></i>
                            Ana Sayfaya Dön
                          </Link>
                          <Link
                            to="/my-account/orders"
                            className="btn btn-lg payment-alt-btn px-24 py-12 rounded-8 d-flex justift-content-center align-items-center"
                          >
                            <i className="ph ph-package me-8"></i>
                            Siparişlerimi Görüntüle
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ShippingOne */}
      <ShippingOne />

      {/* FooterOne */}
      <FooterOne />

      {/* BottomFooter */}
      <BottomFooter />
    </>
  );
};

export default OrderSuccessPage;
